import JSZip from "jszip";

import { Confidence, FormatClassification, Vendor } from "pages/admin/experiments/cropExtraction/types";

const REGION_BY_LAND: Record<string, string> = {
  MV: "Mecklenburg-Vorpommern",
  BB: "Brandenburg",
  HE: "Hessen",
  SN: "Sachsen",
  ST: "Sachsen-Anhalt",
  AT: "Austria",
  NI: "Niedersachsen",
  ND: "Niedersachsen",
  RP: "Rheinland-Pfalz",
  NW: "Nordrhein-Westfalen",
  BY: "Bayern",
  BW: "Baden-Württemberg",
  SH: "Schleswig-Holstein",
  TH: "Thüringen",
  SL: "Saarland",
  HB: "Bremen",
  HH: "Hamburg",
  ALL: "365FarmNet",
};

const lower = (s: string) => s.toLowerCase();
const baseName = (path: string) => path.split("/").pop() || path;

// Some ZIPs (notably Hessen ELAN exports) write paths with mojibake like
// `Antragsfl+?chen Hauptnutzung_POLYGONE/...`. We normalise both by stripping
// non-ASCII and replacing the `+?` glyph that the German umlauts collapse into.
const normalizePath = (path: string) =>
  lower(path)
    .replace(/\+\?/g, "")
    .replace(/[^\x20-\x7e/]/g, "");

export interface ZipInventory {
  jszip: JSZip;
  paths: string[];
  baseNames: string[];
  normalizedPaths: string[];
}

export const buildInventory = (jszip: JSZip): ZipInventory => {
  const paths: string[] = [];
  jszip.forEach((relPath: string, file: JSZip.JSZipObject) => {
    if (!file.dir) paths.push(relPath);
  });
  return {
    jszip,
    paths,
    baseNames: paths.map(baseName).map(lower),
    normalizedPaths: paths.map(normalizePath),
  };
};

type FilePredicate = (path: string, normalized: string, base: string) => boolean;

const hasFile = (inv: ZipInventory, predicate: FilePredicate) => inv.paths.some((p, i) => predicate(p, inv.normalizedPaths[i], inv.baseNames[i]));

const findFile = (inv: ZipInventory, predicate: FilePredicate) => {
  const idx = inv.paths.findIndex((p, i) => predicate(p, inv.normalizedPaths[i], inv.baseNames[i]));
  return idx === -1 ? undefined : inv.paths[idx];
};

const readText = async (jszip: JSZip, path: string): Promise<string> => {
  const file = jszip.file(path);
  return file ? file.async("string") : "";
};

// Reads the column-name list out of a DBF without parsing any row data. DBF
// field descriptors are 32 bytes each starting at offset 32; the name lives
// in the first 11 bytes (NUL-padded), and the descriptor block ends at the
// first 0x0D byte. We bail at 256 fields to keep the work bounded.
const readDbfColumns = async (jszip: JSZip, path: string): Promise<string[] | undefined> => {
  const file = jszip.file(path);
  if (!file) return undefined;
  try {
    const buf = await file.async("uint8array");
    if (buf.length < 33) return undefined;
    const cols: string[] = [];
    for (let off = 32, i = 0; off + 32 <= buf.length && i < 256; off += 32, i += 1) {
      if (buf[off] === 0x0d) break;
      const slice = buf.subarray(off, off + 11);
      let end = 0;
      while (end < slice.length && slice[end] !== 0) end += 1;
      cols.push(new TextDecoder("latin1").decode(slice.subarray(0, end)).toLowerCase());
    }
    return cols;
  } catch {
    return undefined;
  }
};

const CROP_COLUMN_NAMES = new Set([
  "nutz_code",
  "nutzcode",
  "nutzung",
  "ncode",
  "ncode_aktu",
  "vncode",
  "kta_aj",
  "kta_vj",
  "snar_code",
  "sc_ha_code",
  "kultur",
  "kulturart",
  "kultur_fac",
]);

const dbfHasCropColumn = (cols: string[] | undefined): boolean => !!cols && cols.some((c) => CROP_COLUMN_NAMES.has(c));

// Antragsjahr (application year) inference. The year is almost always present
// in the ZIP's path strings, just in different shapes per portal. Each pattern
// below maps to a portal we've actually seen — the comment on each `match()`
// is a real-world example so future contributors can add more without
// guessing.
const MIN_YEAR = 2015;
const MAX_YEAR = new Date().getFullYear() + 1;

const isYearInRange = (n: number): boolean => n >= MIN_YEAR && n <= MAX_YEAR;

const PATH_YEAR_EXTRACTORS: Array<{ name: string; match: (path: string, base: string) => number | undefined }> = [
  // ELAN-RLP MFA: `12345MFA2024ERFFL_FLSL_…dbf`
  { name: "MFA-ERFFL", match: (_p, base) => Number(base.match(/MFA(\d{4})ERFFL/i)?.[1]) || undefined },
  // ELAN-SH "InVeKoS-Online" Export folder: `Export_25012024-182851_Shape/`
  // The 8 digits are DDMMYYYY, so the year is the trailing 4.
  { name: "Export-DDMMYYYY", match: (_p, base) => Number(base.match(/^Export_\d{2}\d{2}(\d{4})-\d{6}_Shape$/i)?.[1]) || undefined },
  // 14-digit YYYYMMDDhhmmss timestamp anywhere in the path (RLP filenames end
  // with one of these; data-experts also embeds them in some packages).
  { name: "YYYYMMDDhhmmss", match: (path) => Number(path.match(/(?<![0-9])(20[1-3]\d)\d{10}(?![0-9])/)?.[1]) || undefined },
  // Plain 4-digit year bounded by non-digits (Schlaege_2024.dbf,
  // Antragsschläge 2025_POLYGONE/, Export_2025_DE03_…_Shape/, etc.).
  // We collect ALL matches because folders/filenames often re-state the year
  // multiple times — picking the modal value rejects "Vorjahr" outliers.
  { name: "4-digit", match: (path) => Number(path.match(/(?<![0-9])(20[1-3]\d)(?![0-9])/)?.[1]) || undefined },
  // data-experts 2-digit basename prefix: `NW25AGR_*.xml`, `BB24FB_*`.
  // Two uppercase letters → federal state, two digits → year, optional suffix.
  {
    name: "2-digit data-experts",
    match: (_p, base) => {
      const m = base.match(/^[A-Z]{2}(\d{2})(?:AGR|FB|HV|HN)/i);
      return m ? 2000 + Number(m[1]) : undefined;
    },
  },
];

export const inferYearFromPaths = (paths: string[]): number | undefined => {
  const counts = new Map<number, number>();
  const bump = (n: number) => counts.set(n, (counts.get(n) ?? 0) + 1);

  for (const path of paths) {
    const base = path.split("/").pop() || path;
    for (const extractor of PATH_YEAR_EXTRACTORS) {
      const candidate = extractor.match(path, base);
      if (candidate && isYearInRange(candidate)) bump(candidate);
    }
  }

  if (counts.size === 0) return undefined;
  // Return the most frequently mentioned year; ties are broken by the largest
  // year (newer wins), since portals occasionally include a sibling reference
  // to the previous year ("Vorjahr") whose citation count never beats the
  // current Antragsjahr but can in degenerate cases tie it.
  let best: number | undefined;
  let bestCount = -1;
  for (const [year, count] of counts) {
    if (count > bestCount || (count === bestCount && (best === undefined || year > best))) {
      best = year;
      bestCount = count;
    }
  }
  return best;
};

interface ClassifyArgs {
  vendor: Vendor;
  family: string;
  supported: boolean;
  confidence: Confidence;
  signals: string[];
  region: string;
  reason?: string;
}

const classify = ({ vendor, family, supported, confidence, signals, region, reason }: ClassifyArgs): FormatClassification => ({
  vendor,
  formatFamily: family,
  supported,
  confidence,
  detectionSignals: signals,
  regionHint: region,
  ...(reason ? { unsupportedReason: reason } : {}),
});

interface Detector {
  name: string;
  match: (inv: ZipInventory) => Promise<FormatClassification | undefined>;
}

const HVU_REASON =
  'This portal export only contains polygon geometries plus application metadata (timestamps, GUIDs, subsidy flags). The crop attribution is stored in the portal backend and is not included in this download. Re-export from your portal selecting the option that includes crops — typically called "Antragsschläge", "Schlagskizze" or similar.';

const DETECTORS: Detector[] = [
  // 1) data-experts AntDV — `*.nn.xml` + `*_parzellen.shp`
  {
    name: "data-experts",
    match: async (inv) => {
      const nnXml = findFile(inv, (_p, _n, base) => base.endsWith(".nn.xml"));
      const parzellenShp = hasFile(inv, (_p, _n, base) => base.endsWith("_parzellen.shp"));
      if (!nnXml || !parzellenShp) return undefined;

      let region = "Germany (data-experts)";
      try {
        const xml = await readText(inv.jszip, nnXml);
        const land = xml.match(/<fa:xsd_land>([^<]+)<\/fa:xsd_land>/i) || xml.match(/<xsd_land>([^<]+)<\/xsd_land>/i);
        if (land) {
          const code = land[1].trim().toUpperCase();
          region = REGION_BY_LAND[code] || `data-experts (${code})`;
        }
      } catch {
        // best effort
      }
      return classify({
        vendor: "data-experts",
        family: "German Portal (data-experts AntDV)",
        supported: true,
        confidence: "high",
        signals: ["*.nn.xml", "*_parzellen.shp"],
        region,
      });
    },
  },

  // 2) FIONA — filenames prefixed with `fiona_`. FIONA is Baden-Württemberg's
  // portal (the original spec mis-attributed it to Berlin). We sniff the DBF
  // column descriptors here: when no crop column is present the export is the
  // geometry-only "Antrag" variant and we surface it as unsupported so the
  // user knows they need to re-export with crops.
  {
    name: "fiona",
    match: async (inv) => {
      const fionaDbf = findFile(inv, (_p, _n, base) => base.startsWith("fiona_") && base.endsWith(".dbf"));
      const fionaAny = hasFile(inv, (_p, _n, base) => base.startsWith("fiona_"));
      if (!fionaAny) return undefined;

      const cols = fionaDbf ? await readDbfColumns(inv.jszip, fionaDbf) : undefined;
      if (cols && !dbfHasCropColumn(cols)) {
        return classify({
          vendor: "fiona-no-crops",
          family: "FIONA (geometry-only export, no crop column)",
          supported: false,
          confidence: "high",
          signals: ["fiona_*.dbf", `${cols.length} cols, none crop-bearing`],
          region: "Baden-Württemberg",
          reason: HVU_REASON,
        });
      }
      return classify({
        vendor: "fiona",
        family: "FIONA (Baden-Württemberg)",
        supported: true,
        confidence: "high",
        signals: ["fiona_* prefix"],
        region: "Baden-Württemberg",
      });
    },
  },

  // 3) Legacy ELAN-RLP MFA + ERFFL filenames
  {
    name: "elan-rlp-mfa",
    match: async (inv) => {
      const hasMfa = hasFile(inv, (_p, _n, base) => base.includes("mfa"));
      const hasErffl = hasFile(inv, (_p, _n, base) => base.includes("erffl"));
      if (!hasMfa || !hasErffl) return undefined;
      return classify({
        vendor: "elan-rlp",
        family: "ELAN-RLP (Rheinland-Pfalz)",
        supported: true,
        confidence: "high",
        signals: ["MFA + ERFFL filenames"],
        region: "Rheinland-Pfalz",
      });
    },
  },

  // 4a) ELAN-NRW (data-experts NN schema) — TS_*.dbf + sibling <nn> XML with
  // <feldblockkonstante>. The federal-state code in that element labels the
  // region so the same parser can serve neighbouring states' equivalents.
  {
    name: "elan-nrw",
    match: async (inv) => {
      const tsDbf = hasFile(inv, (_p, _n, base) => /^ts_.*\.dbf$/.test(base));
      const candidate = findFile(inv, (_p, _n, base) => base.endsWith(".xml") && !base.endsWith(".nn.xml"));
      if (!tsDbf || !candidate) return undefined;
      try {
        const xml = await readText(inv.jszip, candidate);
        if (!/<nn[\s>]/i.test(xml) || !/<feldblockkonstante>/i.test(xml)) return undefined;
        let region = "Nordrhein-Westfalen";
        const land = xml.match(/<land>[^]*?<bezeichnung>([^<]+)<\/bezeichnung>/i);
        const constante = xml.match(/<feldblockkonstante>([^<]+)<\/feldblockkonstante>/i);
        if (land) region = land[1].trim();
        else if (constante) region = REGION_BY_LAND[constante[1].trim().toUpperCase()] || region;
        return classify({
          vendor: "elan-nrw",
          family: "ELAN-NRW (data-experts NN schema)",
          supported: true,
          confidence: "high",
          signals: ["TS_*.dbf", "<nn> XML with <feldblockkonstante>"],
          region,
        });
      } catch {
        return undefined;
      }
    },
  },

  // 4b) ELAN-RLP (SchlaegeExport.dbf) — Rheinland-Pfalz farmer export. The
  // original spec attributed this to NRW, but real samples confirm RLP.
  {
    name: "elan-rlp-schlaege",
    match: async (inv) => {
      if (!hasFile(inv, (_p, _n, base) => base === "schlaegeexport.dbf")) return undefined;
      return classify({
        vendor: "elan-rlp",
        family: "ELAN-RLP (SchlaegeExport.dbf)",
        supported: true,
        confidence: "high",
        signals: ["SchlaegeExport.dbf", "KTA_AJ column"],
        region: "Rheinland-Pfalz",
      });
    },
  },

  // 5) ANDI — `geometrien/` folder + sibling XML with `ns2:hauptantrag` root
  {
    name: "andi",
    match: async (inv) => {
      const hasGeo = hasFile(inv, (_p, n) => n.includes("geometrien/"));
      const candidate = findFile(inv, (_p, _n, base) => base.endsWith(".xml"));
      if (!hasGeo || !candidate) return undefined;
      try {
        const xml = await readText(inv.jszip, candidate);
        if (!/hauptantrag\b/i.test(xml) || !/<schlag\b/i.test(xml)) return undefined;
        return classify({
          vendor: "andi",
          family: "ANDI Portal (Mecklenburg-Vorpommern / Niedersachsen)",
          supported: true,
          confidence: "high",
          signals: ["geometrien/ folder", "<ns2:hauptantrag> XML"],
          region: "Mecklenburg-Vorpommern / Niedersachsen",
        });
      } catch {
        return undefined;
      }
    },
  },

  // 6) iBALIS — both `Feldstueck.dbf` and `Nutzung.dbf` present
  {
    name: "ibalis",
    match: async (inv) => {
      const fs = hasFile(inv, (_p, _n, base) => base === "feldstueck.dbf");
      const nu = hasFile(inv, (_p, _n, base) => base === "nutzung.dbf");
      if (!fs || !nu) return undefined;
      return classify({
        vendor: "ibalis",
        family: "iBALIS (Bayern / Baden-Württemberg)",
        supported: true,
        confidence: "high",
        signals: ["Feldstueck.dbf", "Nutzung.dbf"],
        region: "Bayern / Baden-Württemberg",
      });
    },
  },

  // 7) DIANAweb — filename starts with `Schlaege_`
  {
    name: "dianaweb",
    match: async (inv) => {
      if (!hasFile(inv, (_p, _n, base) => base.startsWith("schlaege_") && base.endsWith(".dbf"))) return undefined;
      return classify({
        vendor: "dianaweb",
        family: "DIANAweb (Sachsen / Saarland)",
        supported: true,
        confidence: "high",
        signals: ["Schlaege_*.dbf"],
        region: "Sachsen / Saarland",
      });
    },
  },

  // 8) InVeKoS-Online "Export_*_Shape" package — same folder layout shipped by
  // several portals (confirmed: Saarland; spec also lists Schleswig-Holstein).
  // Region is derived from the FLIK prefix when we can read it.
  {
    name: "elan-sh",
    match: async (inv) => {
      const exportDbf = findFile(inv, (_p, n, base) => base.endsWith(".dbf") && /export_[^/]*_shape\//.test(n) && base.startsWith("schlag"));
      const exportAny = hasFile(inv, (_p, n) => /export_[^/]*_shape\//.test(n));
      if (!exportAny) return undefined;
      let region = "Saarland / Schleswig-Holstein";
      if (exportDbf) {
        try {
          const buf = await inv.jszip.file(exportDbf)!.async("uint8array");
          // The DBF stores FLIKs as fixed-width latin1 records, so the first
          // occurrence in the first ~16 KB is reliable enough for region ID.
          const sample = new TextDecoder("latin1").decode(buf.subarray(0, Math.min(buf.length, 16384)));
          const flik = sample.match(/DE([A-Z]{2})[0-9A-Z]{10}/);
          if (flik) region = REGION_BY_LAND[flik[1]] || region;
        } catch {
          // best effort
        }
      }
      return classify({
        vendor: "elan-sh",
        family: "InVeKoS-Online Export_*_Shape",
        supported: true,
        confidence: "high",
        signals: ["Export_*_Shape/", "schlag_*.dbf"],
        region,
      });
    },
  },

  // 9a) Hessen / Antragsschläge POLYGONE — DBF inside `Antragsschläge*_POLYGONE/`
  {
    name: "hessen-polygone",
    match: async (inv) => {
      if (!hasFile(inv, (_p, n, base) => base.endsWith(".dbf") && /antragsschl[^/]*_polygone\//.test(n))) return undefined;
      return classify({
        vendor: "hessen-polygone",
        family: "ELAN-Hessen (Antragsschläge_POLYGONE)",
        supported: true,
        confidence: "high",
        signals: ["Antragsschläge*_POLYGONE/*.dbf", "ncode_aktu column"],
        region: "Hessen",
      });
    },
  },

  // 9b) Antragsflächen Hauptnutzung_POLYGONE — geometry-only export (Bayern,
  // Thüringen, parts of Hessen). DBF has only GEOWD_* metadata, no crops.
  {
    name: "hvu-polygone",
    match: async (inv) => {
      if (!hasFile(inv, (_p, n, base) => base.endsWith(".dbf") && /antragsfl[^/]*_polygone\//.test(n))) return undefined;
      let region = "Bayern / Thüringen";
      try {
        const prj = findFile(inv, (_p, n, base) => base.endsWith(".prj") && /antragsfl[^/]*_polygone\//.test(n));
        if (prj) {
          const text = await readText(inv.jszip, prj);
          if (/zone[_ ]?32/i.test(text)) region = "Bayern / Thüringen / Hessen (UTM 32N)";
          else if (/zone[_ ]?33/i.test(text)) region = "Eastern Germany (UTM 33N)";
        }
      } catch {
        // best effort
      }
      return classify({
        vendor: "hvu-polygone",
        family: "Antragsflächen Hauptnutzung_POLYGONE (geometry-only export)",
        supported: false,
        confidence: "high",
        signals: ["Antragsflächen*_POLYGONE/*.dbf", "GEOWD_* schema, no crop column"],
        region,
        reason: HVU_REASON,
      });
    },
  },

  // 10) Polish eWniosekPlus — `dzialkiRef*` present, no crop layer
  {
    name: "polish-ewniosekplus",
    match: async (inv) => {
      if (!hasFile(inv, (_p, n) => n.includes("dzialkireferencyjne") || n.includes("dzialkiref"))) return undefined;
      return classify({
        vendor: "polish-ewniosekplus",
        family: "Polish eWniosekPlus (geometry only, no crops)",
        supported: false,
        confidence: "high",
        signals: ["dzialkiReferencyjne folder"],
        region: "Poland",
      });
    },
  },

  // 11) TatukGIS — any `.bmc` file
  {
    name: "tatuk",
    match: async (inv) => {
      if (!hasFile(inv, (_p, _n, base) => base.endsWith(".bmc"))) return undefined;
      return classify({ vendor: "tatuk", family: "TatukGIS (geometry only)", supported: false, confidence: "high", signals: ["*.bmc"], region: "Unknown" });
    },
  },

  // 12) Niedersachsen Alt — TS_*.dbf without an XML companion
  {
    name: "niedersachsen-alt",
    match: async (inv) => {
      const tsDbf = hasFile(inv, (_p, _n, base) => /^ts_.*\.dbf$/.test(base));
      const anyXml = hasFile(inv, (_p, _n, base) => base.endsWith(".xml"));
      if (!tsDbf || anyXml) return undefined;
      return classify({
        vendor: "niedersachsen-alt",
        family: "Niedersachsen Alt (TS_*.dbf, geometry only)",
        supported: false,
        confidence: "high",
        signals: ["TS_*.dbf without companion XML"],
        region: "Niedersachsen",
      });
    },
  },
];

export const classifyZip = async (jszip: JSZip): Promise<{ classification: FormatClassification; inventory: ZipInventory }> => {
  const inventory = buildInventory(jszip);
  // Year is portal-agnostic — inferring it once from the path list keeps
  // every detector a single concern (vendor + region) and lets future
  // detectors inherit year support for free.
  const year = inferYearFromPaths(inventory.paths);
  // Run all detectors concurrently (avoids `no-await-in-loop`) but preserve
  // the priority order of `DETECTORS` by picking the first non-null result.
  const hits = await Promise.all(DETECTORS.map((d) => d.match(inventory)));
  const hit = hits.find((h): h is FormatClassification => Boolean(h));
  if (hit) return { classification: { ...hit, year }, inventory };
  return {
    classification: { ...classify({ vendor: "unknown", family: "Unknown vendor", supported: false, confidence: "low", signals: [], region: "Unknown" }), year },
    inventory,
  };
};
